import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent, trackPageView, AnalyticsEvent } from '../services/analyticsService';
import { demoService } from '../services/demoService';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showHero, setShowHero] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    // Track landing page view with consistent naming
    trackPageView('/', 'AwakenArc - Level Up Your Life');
    
    // Trigger hero animation on mount
    setTimeout(() => setShowHero(true), 100);
  }, []);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Animate bars only once when feature becomes active
  useEffect(() => {
    if (activeFeature >= 0 && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [activeFeature, hasAnimated]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            setVisibleSections((prev) => new Set(prev).add(sectionId));
            
            // Track section views
            trackEvent('section_viewed', {
              section_name: sectionId,
              section_title: entry.target.getAttribute('data-section-title') || sectionId,
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: '⚔️',
      title: 'Daily Quest System',
      subtitle: '［ Never Miss a Day ］',
      description: 'The System generates daily quests tailored to your level. Complete them to earn EXP, gold, and stat rewards.',
      gradient: 'from-system-blue to-blue-600',
      stats: [
        { label: 'Quest Completion Rate', value: 94 },
        { label: 'Avg. Daily Rewards', value: '+250 EXP' },
      ],
      badge: 'CORE SYSTEM',
    },
    {
      icon: '📊',
      title: 'Stat Allocation System',
      subtitle: '［ Customize Your Build ］',
      description: 'Every level grants stat points. Allocate them strategically across 5 core attributes to create your unique hunter build.',
      gradient: 'from-purple-500 to-pink-600',
      stats: [
        { label: 'STR', value: 45 },
        { label: 'INT', value: 38 },
        { label: 'AGI', value: 52 },
      ],
      badge: 'POWER SYSTEM',
    },
    {
      icon: '🤖',
      title: 'AI Quest Generator',
      subtitle: '［ Infinite Possibilities ］',
      description: 'Advanced AI creates personalized quests based on your goals, skill level, and available time. Each quest is unique.',
      gradient: 'from-green-500 to-emerald-600',
      stats: [
        { label: 'AI Accuracy', value: 98 },
        { label: 'Quests Generated', value: '50K+' },
      ],
      badge: 'AI POWERED',
    },
    {
      icon: '🏆',
      title: 'Progression System',
      subtitle: '［ E-Rank → Shadow Monarch ］',
      description: 'Unlock achievements, earn titles, and climb through hunter ranks. Each milestone brings exclusive rewards and abilities.',
      gradient: 'from-yellow-500 to-orange-600',
      stats: [
        { label: 'Total Achievements', value: 50 },
        { label: 'Epic Titles', value: 12 },
      ],
      badge: 'ELITE PATH',
    },
  ];

  const ranks = ['E', 'D', 'C', 'B', 'A', 'S'];

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    trackEvent('scroll_to_top_clicked', {
      button_location: 'scroll_to_top',
      scroll_position: window.pageYOffset
    });
  };

  const handleDemoClick = () => {
    handleStartDemo();
  };

  const handleStartDemo = () => {
    // Create demo user and navigate to dashboard
    const demoUser = demoService.createDemoUser();
    demoService.saveDemoData(demoUser);
    
    // Store demo user in session storage for the app to use
    sessionStorage.setItem('demo_user', JSON.stringify(demoUser));
    sessionStorage.setItem('is_demo_mode', 'true');
    
    setIsDemoMode(true);
    trackEvent('demo_started', {
      demo_user_level: demoUser.profile.level,
      demo_quests_count: demoUser.quests.length,
    });
    
    // Use window.location.href to force full page reload so App component re-initializes
    // This ensures isDemoMode state is properly set from session storage
    window.location.href = '/dashboard';
  };


  const testimonials = [
    {
      rank: 'A',
      name: 'Shadow Hunter',
      message: 'I went from E-Rank to A-Rank in 3 months. This system changed my life!',
      level: 67,
    },
    {
      rank: 'S',
      name: 'Monarch Aspirant',
      message: 'The AI quest generator keeps me motivated every single day. Best productivity tool ever!',
      level: 89,
    },
    {
      rank: 'B',
      name: 'Rising Hunter',
      message: 'Finally a system that makes self-improvement feel like an RPG game. Absolutely addicted!',
      level: 45,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-dark-900 to-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-800/80 backdrop-blur-lg border-b border-system-blue/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20 lg:h-24">
            <div 
              className="flex items-center gap-2 sm:gap-3 lg:gap-4 group cursor-pointer py-2"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {/* Premium Logo */}
              <img 
                src="/logo/logo.png" 
                alt="AwakenArc Logo" 
                className="h-12 sm:h-16 lg:h-20 w-auto object-contain transition-all duration-500 group-hover:scale-[1.02] group-hover:drop-shadow-[0_0_30px_rgba(59,130,246,0.9)] group-hover:brightness-110"
              />
              
              {/* Brand Text */}
              <div className="flex flex-col justify-center">
                <span className="font-orbitron text-base sm:text-xl lg:text-2xl font-bold text-white leading-none tracking-tight transition-colors duration-300 group-hover:text-system-blue">
                  Awaken<span className="text-system-blue">Arc</span>
                </span>
                <span className="font-rajdhani text-[10px] sm:text-xs text-gray-400 tracking-wide mt-0.5 sm:mt-1 transition-colors duration-300 group-hover:text-gray-300">
                  Level up your life
                </span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4 lg:gap-8">
              <a 
                href="#features" 
                onClick={() => {
                  trackEvent('desktop_navigation_clicked', {
                    link_text: 'Features',
                    link_location: 'desktop_nav',
                  });
                }}
                className="font-rajdhani text-xs lg:text-sm font-medium text-gray-300 hover:text-white transition-all duration-300 relative group"
              >
                Features
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-system-blue group-hover:w-full transition-all duration-300"></span>
              </a>
              <a 
                href="#how-it-works" 
                onClick={() => {
                  trackEvent('desktop_navigation_clicked', {
                    link_text: 'How It Works',
                    link_location: 'desktop_nav',
                  });
                }}
                className="font-rajdhani text-xs lg:text-sm font-medium text-gray-300 hover:text-white transition-all duration-300 relative group"
              >
                How It Works
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-system-blue group-hover:w-full transition-all duration-300"></span>
              </a>
              <a 
                href="#pricing" 
                onClick={() => {
                  trackEvent('desktop_navigation_clicked', {
                    link_text: 'Pricing',
                    link_location: 'desktop_nav',
                  });
                }}
                className="font-rajdhani text-xs lg:text-sm font-medium text-gray-300 hover:text-white transition-all duration-300 relative group"
              >
                Pricing
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-system-blue group-hover:w-full transition-all duration-300"></span>
              </a>
              <button 
                onClick={() => {
                  trackEvent('desktop_navigation_clicked', {
                    link_text: 'Support',
                    link_location: 'desktop_nav',
                  });
                  navigate('/support');
                }} 
                className="font-rajdhani text-xs lg:text-sm font-medium text-gray-300 hover:text-white transition-all duration-300 relative group"
              >
                Support
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-system-blue group-hover:w-full transition-all duration-300"></span>
              </button>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>

              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Login',
                    button_location: 'navigation',
                    cta_type: 'navigation',
                  });
                  navigate('/auth');
                }}
                className="font-rajdhani text-xs sm:text-sm font-medium px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 text-gray-300 hover:text-white transition-all duration-300"
              >
                Login
              </button>
              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Sign Up',
                    button_location: 'navigation',
                    cta_type: 'primary',
                  });
                  navigate('/auth');
                }}
                className="font-rajdhani text-xs sm:text-sm font-semibold px-4 sm:px-5 lg:px-6 py-2 sm:py-2.5 text-white rounded-2xl transition-all duration-500 hover:scale-105 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(to bottom, rgb(59 130 246 / 0.9), rgb(37 99 235 / 0.9))',
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                <span className="relative z-10">Sign Up</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 py-4 space-y-3 bg-gray-900/95 backdrop-blur-lg border-t border-system-blue/20">
            <a
              href="#features"
              onClick={() => {
                setMobileMenuOpen(false);
                trackEvent('mobile_navigation_clicked', {
                  link_text: 'Features',
                  link_location: 'mobile_menu',
                });
              }}
              className="block font-rajdhani text-sm font-medium text-gray-300 hover:text-white hover:bg-system-blue/10 px-4 py-3 rounded-lg transition-all duration-300"
            >
              Features
            </a>
            <a
              href="#how-it-works"
              onClick={() => {
                setMobileMenuOpen(false);
                trackEvent('mobile_navigation_clicked', {
                  link_text: 'How It Works',
                  link_location: 'mobile_menu',
                });
              }}
              className="block font-rajdhani text-sm font-medium text-gray-300 hover:text-white hover:bg-system-blue/10 px-4 py-3 rounded-lg transition-all duration-300"
            >
              How It Works
            </a>
            <a
              href="#pricing"
              onClick={() => {
                setMobileMenuOpen(false);
                trackEvent('mobile_navigation_clicked', {
                  link_text: 'Pricing',
                  link_location: 'mobile_menu',
                });
              }}
              className="block font-rajdhani text-sm font-medium text-gray-300 hover:text-white hover:bg-system-blue/10 px-4 py-3 rounded-lg transition-all duration-300"
            >
              Pricing
            </a>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                trackEvent('mobile_navigation_clicked', {
                  link_text: 'Support',
                  link_location: 'mobile_menu',
                });
                navigate('/support');
              }}
              className="w-full text-left font-rajdhani text-sm font-medium text-gray-300 hover:text-white hover:bg-system-blue/10 px-4 py-3 rounded-lg transition-all duration-300"
            >
              Support
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-20 lg:pt-24">
        {/* Animated Background Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-system-blue rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`,
                opacity: Math.random() * 0.5 + 0.3,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* System Message Animation */}
          <div
            className={`transform transition-all duration-1000 ${
              showHero ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            {/* "System" Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mt-8 sm:mt-12 mb-6 sm:mb-8 animate-pulse-glow">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse"></div>
              <span className="font-orbitron text-xs sm:text-sm text-system-blue uppercase tracking-wider">
                ［ #1 Gamified Productivity ］
              </span>
            </div>

            {/* Product Hunt Badge - Commented out until official launch */}
            {/* <div className="mb-6 sm:mb-8 flex justify-center">
              <a 
                href="https://www.producthunt.com/products/awakenarc?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-awakenarc" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent('product_hunt_badge_clicked', {
                    badge_type: 'featured',
                    badge_location: 'hero_section',
                  });
                }}
                className="transition-transform duration-300 hover:scale-105 hover:shadow-lg"
              >
                <img 
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1028292&theme=light&t=1760863996882" 
                  alt="AwakenArc - Gamified productivity system inspired by Solo Leveling | Product Hunt" 
                  style={{ width: '250px', height: '54px' }} 
                  width="250" 
                  height="54"
                  className="rounded-lg shadow-lg"
                />
              </a>
            </div> */}

            {/* Main Headline */}
            <h1 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-white leading-tight px-2">
              Turn Your Daily Tasks Into
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-system-blue via-blue-400 to-purple-500 animate-text-shimmer">
                Epic Quest Adventures
              </span>
            </h1>

            {/* Subheadline */}
            <p className="font-rajdhani text-base sm:text-xl md:text-2xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              The gamified productivity app that makes self-improvement feel like playing an RPG
            </p>

            {/* Dashboard Screenshot with 3D Glass Effect */}
            <div className="mb-8 sm:mb-12 px-4">
              <div className="max-w-5xl mx-auto relative group">
                {/* Main 3D Glass Container */}
                <div 
                  className="relative rounded-3xl overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.5), rgba(51, 65, 85, 0.4))',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: `
                      0 10px 40px rgba(0, 0, 0, 0.7),
                      0 4px 16px rgba(0, 0, 0, 0.5),
                      inset 0 1px 2px rgba(255, 255, 255, 0.15),
                      inset 0 -1px 2px rgba(0, 0, 0, 0.3)
                    `
                  }}
                >
                  {/* Glass inner glow layer */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), transparent 40%)',
                      backdropFilter: 'blur(20px)'
                    }}
                  />
                  
                  {/* Outer border highlight for 3D effect */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to top, rgba(255, 255, 255, 0.12) 0%, transparent 20%)',
                    }}
                  />
                  
                  {/* Screenshot Image */}
                  <img 
                    src="/screenshots/dashboard.png" 
                    alt="AwakenArc Dashboard" 
                    className="relative w-full h-auto transition-all duration-500 group-hover:scale-[1.02]"
                    style={{ 
                      imageRendering: 'crisp-edges'
                    }}
                  />
                  
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill='none' stroke='%233b82f6' stroke-width='1' opacity='0.3'/%3E%3C/svg%3E")`,
                      backgroundSize: '40px 40px'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16 px-4">
              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Get Started Free',
                    button_location: 'hero_section',
                    cta_type: 'primary',
                  });
                  navigate('/auth');
                }}
                className="group relative w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-b from-blue-800/70 via-blue-700/60 to-blue-900/70 text-white font-rajdhani text-base sm:text-lg font-bold rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105"
                style={{
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  borderTop: '2px solid rgba(100, 150, 255, 0.4)',
                  borderLeft: '2px solid rgba(100, 150, 255, 0.4)',
                  borderRight: '2px solid rgba(37, 99, 235, 0.6)',
                  borderBottom: '2px solid rgba(37, 99, 235, 0.6)',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), 0 2px 8px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                <span className="relative z-10 flex items-center justify-center gap-2">
                  ⚡ Get Started Free
                </span>
              </button>
              
              <button
                onClick={handleDemoClick}
                className="group relative w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-white font-rajdhani text-base sm:text-lg font-bold rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105"
                style={{
                  background: 'linear-gradient(to bottom, rgb(217 119 8 / 0.9), rgb(234 179 8 / 0.9), rgb(251 191 36 / 0.9))',
                  border: '1px solid rgba(251, 191, 36, 0.6)',
                  borderTop: '2px solid rgba(253, 230, 138, 0.5)',
                  borderLeft: '2px solid rgba(253, 230, 138, 0.5)',
                  borderRight: '2px solid rgba(180, 83, 9, 0.7)',
                  borderBottom: '2px solid rgba(180, 83, 9, 0.7)',
                  boxShadow: '0 6px 20px rgba(251, 191, 36, 0.5), 0 2px 8px rgba(234, 179, 8, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.25), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/15 via-transparent to-black/15 rounded-2xl"></div>
                <span className="relative z-10 flex items-center justify-center gap-2">
                  🎮 Try Demo Now
                </span>
              </button>
              
              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'See the System',
                    button_location: 'hero_section',
                    cta_type: 'secondary',
                  });
                  document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-white font-rajdhani text-base sm:text-lg font-bold rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105 relative"
                style={{
                  background: 'linear-gradient(to bottom, rgb(51 65 85 / 0.4), rgb(30 41 59 / 0.4), rgb(15 23 42 / 0.4))',
                  border: '1px solid rgba(100, 116, 139, 0.6)',
                  borderTop: '2px solid rgba(100, 116, 139, 0.4)',
                  borderLeft: '2px solid rgba(100, 116, 139, 0.4)',
                  borderRight: '2px solid rgba(71, 85, 105, 0.6)',
                  borderBottom: '2px solid rgba(71, 85, 105, 0.6)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-2xl"></div>
                <span className="relative z-10">👁️ See the System</span>
              </button>
            </div>

            {/* Stats - Commented out until we have real user data */}
            {/* <div className="grid grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-3xl mx-auto px-4">
              <div className="text-center group cursor-default">
                <div className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-system-blue mb-1 sm:mb-2 transition-all duration-300 group-hover:scale-110 group-hover:text-blue-400">
                  10K+
                </div>
                <div className="font-rajdhani text-gray-400 uppercase text-[10px] sm:text-xs md:text-sm transition-colors duration-300 group-hover:text-gray-300">Awakened Hunters</div>
              </div>
              <div className="text-center group cursor-default">
                <div className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-system-blue mb-1 sm:mb-2 transition-all duration-300 group-hover:scale-110 group-hover:text-blue-400">
                  1M+
                </div>
                <div className="font-rajdhani text-gray-400 uppercase text-[10px] sm:text-xs md:text-sm transition-colors duration-300 group-hover:text-gray-300">Quests Completed</div>
              </div>
              <div className="text-center group cursor-default">
                <div className="font-orbitron text-2xl sm:text-3xl md:text-4xl font-bold text-system-blue mb-1 sm:mb-2 transition-all duration-300 group-hover:scale-110 group-hover:text-blue-400">
                  500+
                </div>
                <div className="font-rajdhani text-gray-400 uppercase text-[10px] sm:text-xs md:text-sm transition-colors duration-300 group-hover:text-gray-300">S-Rank Hunters</div>
              </div>
            </div> */}
          </div>
        </div>
      </section>


      {/* Why Different Section */}
      <section 
        id="why-different" 
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-dark-800/30"
        data-animate
        data-section-title="Why Different"
      >
        <div className="max-w-6xl mx-auto">
          <div 
            className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${
              visibleSections.has('why-different') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-500/10 border border-red-500/30 rounded-full mb-4 sm:mb-6 animate-pulse-glow">
              <span className="font-orbitron text-xs sm:text-sm text-red-400 uppercase tracking-wider">
                ［ The Problem ］
              </span>
            </div>
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
              Why Other <span className="text-red-500 animate-pulse">Productivity Tools</span> Fail You
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              You've tried them all. Here's why they never stick.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Pain Points */}
            <div 
              className={`space-y-6 sm:space-y-8 transition-all duration-1000 ${
                visibleSections.has('why-different') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-10'
              }`}
              style={{ transitionDelay: visibleSections.has('why-different') ? '200ms' : '0ms' }}
            >
              <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-6">
                The <span className="text-red-500 animate-pulse">Reality</span> You Know Too Well
              </h3>
              
              <div className="space-y-4 sm:space-y-6">
                <div 
                  className="group flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-2xl hover:border-red-500/70 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-red-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-red-500/30 transition-all duration-300">
                    <span className="text-red-400 text-lg sm:text-xl group-hover:animate-bounce">😴</span>
                  </div>
                  <div>
                    <h4 className="font-orbitron text-base sm:text-lg font-bold text-white mb-2 group-hover:text-red-300 transition-colors">
                      Boring & Generic
                    </h4>
                    <p className="font-rajdhani text-sm sm:text-base text-gray-400 group-hover:text-gray-300 transition-colors">
                      "Complete 5 tasks today" - wow, so exciting. No wonder you abandon them after 3 days.
                    </p>
                  </div>
                </div>

                <div 
                  className="group flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-2xl hover:border-red-500/70 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-red-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-red-500/30 transition-all duration-300">
                    <span className="text-red-400 text-lg sm:text-xl group-hover:animate-bounce">🎯</span>
                  </div>
                  <div>
                    <h4 className="font-orbitron text-base sm:text-lg font-bold text-white mb-2 group-hover:text-red-300 transition-colors">
                      No Clear Progression
                    </h4>
                    <p className="font-rajdhani text-sm sm:text-base text-gray-400 group-hover:text-gray-300 transition-colors">
                      You check off boxes but feel like you're going nowhere. Where's the sense of achievement?
                    </p>
                  </div>
                </div>

                <div 
                  className="group flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-2xl hover:border-red-500/70 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-red-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-red-500/30 transition-all duration-300">
                    <span className="text-red-400 text-lg sm:text-xl group-hover:animate-bounce">🤖</span>
                  </div>
                  <div>
                    <h4 className="font-orbitron text-base sm:text-lg font-bold text-white mb-2 group-hover:text-red-300 transition-colors">
                      One-Size-Fits-All
                    </h4>
                    <p className="font-rajdhani text-sm sm:text-base text-gray-400 group-hover:text-gray-300 transition-colors">
                      Generic templates that don't understand YOUR goals, YOUR schedule, or YOUR personality.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* AwakenArc Solution */}
            <div 
              className={`space-y-6 sm:space-y-8 transition-all duration-1000 ${
                visibleSections.has('why-different') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-10'
              }`}
              style={{ transitionDelay: visibleSections.has('why-different') ? '400ms' : '0ms' }}
            >
              <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-6">
                The <span className="text-system-blue animate-pulse">AwakenArc</span> Difference
              </h3>
              
              <div className="space-y-4 sm:space-y-6">
                <div 
                  className="group flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-2xl hover:border-blue-500/70 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-system-blue/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-system-blue/30 transition-all duration-300">
                    <span className="text-system-blue text-lg sm:text-xl group-hover:animate-bounce">⚔️</span>
                  </div>
                  <div>
                    <h4 className="font-orbitron text-base sm:text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                      Epic Quest System
                    </h4>
                    <p className="font-rajdhani text-sm sm:text-base text-gray-400 group-hover:text-gray-300 transition-colors">
                      Turn mundane tasks into legendary quests. Every completion feels like a boss battle victory.
                    </p>
                  </div>
                </div>

                <div 
                  className="group flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-2xl hover:border-blue-500/70 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-system-blue/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-system-blue/30 transition-all duration-300">
                    <span className="text-system-blue text-lg sm:text-xl group-hover:animate-bounce">📈</span>
                  </div>
                  <div>
                    <h4 className="font-orbitron text-base sm:text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                      Real Progression
                    </h4>
                    <p className="font-rajdhani text-sm sm:text-base text-gray-400 group-hover:text-gray-300 transition-colors">
                      Level up, earn titles, unlock achievements. See your transformation from E-Rank to S-Rank.
                    </p>
                  </div>
                </div>

                <div 
                  className="group flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-blue-700/70 border-b-blue-700/70 rounded-2xl hover:border-blue-500/70 transition-all duration-500 hover:-translate-y-1"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-system-blue/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-system-blue/30 transition-all duration-300">
                    <span className="text-system-blue text-lg sm:text-xl group-hover:animate-bounce">🤖</span>
                  </div>
                  <div>
                    <h4 className="font-orbitron text-base sm:text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                      AI-Powered Personalization
                    </h4>
                    <p className="font-rajdhani text-sm sm:text-base text-gray-400 group-hover:text-gray-300 transition-colors">
                      AI that understands YOU. Generates quests based on your goals, time, and personality.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div 
            className={`text-center mt-12 sm:mt-16 transition-all duration-1000 ${
              visibleSections.has('why-different') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: visibleSections.has('why-different') ? '600ms' : '0ms' }}
          >
            <div 
              className="group bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 sm:p-8 hover:border-system-blue/60 transition-all duration-500"
              style={{
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors">
                Ready to <span className="text-system-blue animate-pulse">Break Free</span> from Generic Tools?
              </h3>
              <p className="font-rajdhani text-base sm:text-lg text-gray-400 mb-6 max-w-2xl mx-auto group-hover:text-gray-300 transition-colors">
                Join the hunters who've discovered the difference between checking boxes and becoming legendary.
              </p>
              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Start Your Awakening',
                    button_location: 'why_different_section',
                    cta_type: 'primary',
                  });
                  navigate('/auth');
                }}
                className="group relative px-8 py-4 bg-gradient-to-b from-blue-800/70 via-blue-700/60 to-blue-900/70 text-white font-rajdhani text-lg font-bold rounded-2xl overflow-hidden transition-all duration-500 hover:scale-105"
                style={{
                  border: '1px solid rgba(59, 130, 246, 0.5)',
                  borderTop: '2px solid rgba(100, 150, 255, 0.4)',
                  borderLeft: '2px solid rgba(100, 150, 255, 0.4)',
                  borderRight: '2px solid rgba(37, 99, 235, 0.6)',
                  borderBottom: '2px solid rgba(37, 99, 235, 0.6)',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), 0 2px 8px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                <span className="relative z-10 flex items-center justify-center gap-2">
                  ⚡ Start Your Awakening
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Hidden for now */}
      <section 
        id="features" 
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-dark-800/30 hidden"
        data-animate
        data-section-title="Features"
      >
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-10 sm:mb-12 lg:mb-16 transition-all duration-1000 ${
              visibleSections.has('features') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mb-4 sm:mb-6">
              <span className="font-orbitron text-xs sm:text-sm text-system-blue uppercase tracking-wider">
                ［ System Features ］
              </span>
            </div>
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              Your Path to <span className="text-system-blue">Power</span>
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto px-4">
              Every great hunter needs the right tools. The System provides everything you need to level up.
            </p>
          </div>

          {/* Apple-Inspired Full-Width Feature Layout with Alternating Sides */}
          <div className="space-y-16 sm:space-y-20 lg:space-y-24">
            {features.map((feature, index) => {
              // Determine if visual should be on left or right (alternating)
              const isVisualOnLeft = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`group transition-all duration-1000 ${
                    visibleSections.has('features')
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-20'
                  }`}
                  style={{ 
                    transitionDelay: visibleSections.has('features') ? `${index * 150}ms` : '0ms'
                  }}
                >
                  <div className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-16 ${!isVisualOnLeft ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Visual Media */}
                    <div className="w-full lg:w-5/12 relative">
                      <div className="relative aspect-video bg-gradient-to-br from-system-blue/5 to-purple-600/5 rounded-2xl overflow-hidden border border-system-blue/10 group-hover:border-system-blue/30 transition-all duration-500 shadow-lg">
                        {/* Placeholder for GIF/Video */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-8xl mb-4 opacity-30">{feature.icon}</div>
                            <p className="font-orbitron text-sm text-system-blue/40 uppercase tracking-wider">
                              Video / GIF Placeholder
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="w-full lg:w-7/12 flex flex-col justify-center">
                      {/* Badge */}
                      <div className="mb-4 inline-block w-fit">
                        <div className={`px-4 py-1.5 bg-gradient-to-r ${feature.gradient} rounded-full opacity-90`}>
                          <span className="font-orbitron text-xs text-white uppercase tracking-widest">
                            {feature.badge}
                          </span>
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h3 className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                        {feature.title}
                      </h3>
                      
                      {/* Subtitle */}
                      <p className="font-orbitron text-sm text-system-blue uppercase tracking-widest mb-6">
                        {feature.subtitle}
                      </p>
                      
                      {/* Description */}
                      <p className="font-rajdhani text-base sm:text-lg text-gray-300 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Transformation Showcase */}
          <div className="mt-12 sm:mt-16 lg:mt-20 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-system-blue/10 to-yellow-500/5 rounded-3xl blur-3xl"></div>
            <div className="relative bg-dark-900/60 backdrop-blur-sm border border-system-blue/30 rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12">
              <div className="text-center mb-8 sm:mb-10 lg:mb-12">
                <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mb-3 sm:mb-4">
                  <span className="font-orbitron text-[10px] sm:text-xs text-system-blue uppercase tracking-wider">
                    ［ Your Transformation ］
                  </span>
                </div>
                <h3 className="font-orbitron text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 px-2">
                  From <span className="text-red-500">Weak</span> to <span className="text-yellow-400">Legendary</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
                {/* Before */}
                <div className="relative">
                  <div className="absolute -top-3 sm:-top-4 left-3 sm:left-4 px-3 sm:px-4 py-0.5 sm:py-1 bg-red-600/20 border border-red-500 rounded-full text-red-400 font-orbitron text-[10px] sm:text-xs font-bold">
                    BEFORE
                  </div>
                  <div className="bg-dark-800/50 border border-red-500/30 rounded-xl p-4 sm:p-5 lg:p-6">
                    <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600/20 border border-red-500 rounded-lg flex items-center justify-center font-orbitron text-lg sm:text-xl font-bold text-red-400">
                        E
                      </div>
                      <div>
                        <div className="font-orbitron text-xs sm:text-sm text-gray-500">E-RANK HUNTER</div>
                        <div className="font-rajdhani text-[10px] sm:text-xs text-gray-600">The Weakest</div>
                      </div>
                    </div>

                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">Level</span>
                        <span className="font-orbitron font-bold text-red-400 text-sm sm:text-base">1</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">STR</span>
                        <span className="font-orbitron font-bold text-gray-500 text-sm sm:text-base">10</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">Quests Completed</span>
                        <span className="font-orbitron font-bold text-gray-500 text-sm sm:text-base">0</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">Achievements</span>
                        <span className="font-orbitron font-bold text-gray-500 text-sm sm:text-base">0</span>
                      </div>
                      <div className="pt-2 sm:pt-3 border-t border-gray-700">
                        <span className="font-rajdhani text-[10px] sm:text-xs text-gray-600">❌ No motivation</span><br/>
                        <span className="font-rajdhani text-[10px] sm:text-xs text-gray-600">❌ No clear goals</span><br/>
                        <span className="font-rajdhani text-[10px] sm:text-xs text-gray-600">❌ No progress tracking</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center">
                  <div className="text-4xl lg:text-6xl text-system-blue animate-pulse">→</div>
                </div>

                {/* After */}
                <div className="relative">
                  <div className="absolute -top-3 sm:-top-4 left-3 sm:left-4 px-3 sm:px-4 py-0.5 sm:py-1 bg-yellow-600/20 border border-yellow-500 rounded-full text-yellow-400 font-orbitron text-[10px] sm:text-xs font-bold animate-pulse">
                    AFTER 90 DAYS
                  </div>
                  <div className="bg-dark-800/50 border-2 border-yellow-500 rounded-xl p-4 sm:p-5 lg:p-6 shadow-[0_0_30px_rgba(234,179,8,0.3)]">
                    <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center font-orbitron text-lg sm:text-xl font-bold text-black shadow-lg shadow-yellow-500/50">
                        S
                      </div>
                      <div>
                        <div className="font-orbitron text-xs sm:text-sm text-yellow-400">S-RANK HUNTER</div>
                        <div className="font-rajdhani text-[10px] sm:text-xs text-gray-400">Elite Status</div>
                      </div>
                    </div>

                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">Level</span>
                        <span className="font-orbitron font-bold text-yellow-400 text-sm sm:text-base">85</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">STR</span>
                        <span className="font-orbitron font-bold text-green-400 text-sm sm:text-base">89</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">Quests Completed</span>
                        <span className="font-orbitron font-bold text-green-400 text-sm sm:text-base">247</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-rajdhani text-gray-400 text-xs sm:text-sm">Achievements</span>
                        <span className="font-orbitron font-bold text-green-400 text-sm sm:text-base">32</span>
                      </div>
                      <div className="pt-2 sm:pt-3 border-t border-yellow-700/30">
                        <span className="font-rajdhani text-[10px] sm:text-xs text-green-400">✓ Daily momentum</span><br/>
                        <span className="font-rajdhani text-[10px] sm:text-xs text-green-400">✓ Crystal clear goals</span><br/>
                        <span className="font-rajdhani text-[10px] sm:text-xs text-green-400">✓ Measurable progress</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 text-center">
                <p className="font-rajdhani text-sm sm:text-base text-gray-400 mb-3 sm:mb-4 px-4">
                  Start your transformation journey and become the hunter you were meant to be
                </p>
                <button
                  onClick={() => {
                    trackEvent('cta_clicked', {
                      button_text: 'Start Your Transformation',
                      button_location: 'transformation_section',
                      cta_type: 'primary',
                    });
                    navigate('/auth');
                  }}
                  className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 text-white font-rajdhani text-sm sm:text-base font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(to bottom, rgb(59 130 246 / 0.9), rgb(37 99 235 / 0.9), rgb(30 64 175 / 0.9))',
                    border: '1px solid rgba(59, 130, 246, 0.5)',
                    borderTop: '2px solid rgba(100, 150, 255, 0.4)',
                    borderLeft: '2px solid rgba(100, 150, 255, 0.4)',
                    borderRight: '2px solid rgba(37, 99, 235, 0.6)',
                    borderBottom: '2px solid rgba(37, 99, 235, 0.6)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  <span className="relative z-10">Start Your Transformation</span>
                </button>
              </div>
            </div>
          </div>

          {/* Rank Progression Visual */}
          <div className="mt-12 sm:mt-16 lg:mt-20">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2 px-2">
                Rank Progression System
              </h3>
              <p className="font-rajdhani text-sm sm:text-base text-gray-400 px-4">
                Climb through the ranks and prove your worth
              </p>
            </div>
            
            <div className="flex items-center justify-center gap-2 sm:gap-3 lg:gap-4 flex-wrap px-2">
              {ranks.map((rank, index) => (
                <React.Fragment key={rank}>
                  <div className="relative group">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex items-center justify-center rounded-lg font-orbitron text-lg sm:text-xl lg:text-2xl font-bold transition-all duration-300 ${
                      rank === 'S'
                        ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-lg shadow-yellow-500/50'
                        : rank === 'A'
                        ? 'bg-gradient-to-br from-purple-500 to-pink-600 text-white'
                        : 'bg-dark-800 border border-gray-600 text-gray-400 group-hover:border-system-blue group-hover:text-system-blue'
                    }`}>
                      {rank}
                    </div>
                    <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap font-rajdhani text-[10px] sm:text-xs text-gray-500">
                      Level {index * 20}+
                    </div>
                  </div>
                  {index < ranks.length - 1 && (
                    <div className="text-lg sm:text-xl lg:text-2xl text-gray-600">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mb-4 sm:mb-6">
              <span className="font-orbitron text-xs sm:text-sm text-system-blue uppercase tracking-wider">
                ［ Hunter Testimonials ］
              </span>
            </div>
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              <span className="text-system-blue">Hunter</span> Testimonials
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-400 px-4">
              See what our community of hunters has to say
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="relative bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-5 lg:p-6 hover:border-system-blue/60 transition-all duration-300"
                style={{ 
                  animationDelay: `${index * 200}ms`,
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
                }}
              >
                {/* System Message Header */}
                <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse flex-shrink-0"></div>
                    <span className="font-orbitron text-[10px] sm:text-xs text-system-blue uppercase truncate">System Message</span>
                  </div>
                  <div className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-orbitron text-[10px] sm:text-xs font-bold whitespace-nowrap flex-shrink-0 ${
                    testimonial.rank === 'S'
                      ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                      : testimonial.rank === 'A'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                      : 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
                  }`}>
                    {testimonial.rank}-RANK
                  </div>

                </div>

                {/* Testimonial */}
                <p className="font-rajdhani text-sm sm:text-base text-gray-300 mb-3 sm:mb-4 leading-relaxed">
                  "{testimonial.message}"
                </p>

                {/* Hunter Info */}
                <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-700">
                  <div>
                    <div className="font-rajdhani font-bold text-white text-sm sm:text-base">{testimonial.name}</div>
                    <div className="font-rajdhani text-xs sm:text-sm text-gray-400">Level {testimonial.level}</div>
                  </div>
                  <div className="text-2xl sm:text-3xl">⚔️</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section 
        id="how-it-works" 
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-dark-800/30"
        data-animate
        data-section-title="How It Works"
      >
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-10 sm:mb-12 lg:mb-16 transition-all duration-1000 ${
              visibleSections.has('how-it-works') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mb-4 sm:mb-6">
              <span className="font-orbitron text-xs sm:text-sm text-system-blue uppercase tracking-wider">
                ［ System Process ］
              </span>
            </div>
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              How the <span className="text-system-blue">System</span> Works
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-400 px-4">
              Three simple steps to begin your transformation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Step 1 */}
            <div 
              className={`relative text-center group transition-all duration-700 ${
                visibleSections.has('how-it-works')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-20'
              }`}
              style={{ transitionDelay: visibleSections.has('how-it-works') ? '0ms' : '0ms' }}
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-system-blue to-blue-600 rounded-full flex items-center justify-center font-orbitron text-lg sm:text-xl lg:text-2xl font-bold text-white shadow-[0_0_20px_rgba(59,130,246,0.5)] z-10 transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(59,130,246,0.8)]">
                1
              </div>
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 pt-10 sm:p-8 sm:pt-12 hover:border-system-blue/60 transition-all duration-500 hover:-translate-y-1"
                style={{
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                }}
              >
                <div className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-110">⚡</div>
                <h3 className="font-orbitron text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3">
                  Accept the System
                </h3>
                <p className="font-rajdhani text-sm sm:text-base text-gray-400 leading-relaxed">
                  Sign up and create your hunter profile. The System will evaluate your current rank and set your starting stats.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div 
              className={`relative text-center group transition-all duration-700 ${
                visibleSections.has('how-it-works')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-20'
              }`}
              style={{ transitionDelay: visibleSections.has('how-it-works') ? '200ms' : '0ms' }}
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center font-orbitron text-lg sm:text-xl lg:text-2xl font-bold text-white shadow-[0_0_20px_rgba(168,85,247,0.5)] z-10 transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(168,85,247,0.8)]">
                2
              </div>
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 pt-10 sm:p-8 sm:pt-12 hover:border-system-blue/60 transition-all duration-500 hover:-translate-y-1"
                style={{
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                }}
              >
                <div className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-110">⚔️</div>
                <h3 className="font-orbitron text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3">
                  Complete Daily Quests
                </h3>
                <p className="font-rajdhani text-sm sm:text-base text-gray-400 leading-relaxed">
                  Tackle daily quests, use the AI generator for custom challenges, and complete tasks to earn EXP and rewards.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div 
              className={`relative text-center group transition-all duration-700 ${
                visibleSections.has('how-it-works')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-20'
              }`}
              style={{ transitionDelay: visibleSections.has('how-it-works') ? '400ms' : '0ms' }}
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center font-orbitron text-lg sm:text-xl lg:text-2xl font-bold text-white shadow-[0_0_20px_rgba(34,197,94,0.5)] z-10 transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(34,197,94,0.8)]">
                3
              </div>
              <div 
                className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 pt-10 sm:p-8 sm:pt-12 hover:border-system-blue/60 transition-all duration-500 hover:-translate-y-1"
                style={{
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                }}
              >
                <div className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-110">📈</div>
                <h3 className="font-orbitron text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3">
                  Level Up & Evolve
                </h3>
                <p className="font-rajdhani text-sm sm:text-base text-gray-400 leading-relaxed">
                  Allocate stat points, unlock achievements, climb ranks, and transform into the ultimate version of yourself.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section 
        id="demo" 
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
        data-animate
        data-section-title="Demo"
      >
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-10 sm:mb-12 lg:mb-16 transition-all duration-1000 ${
              visibleSections.has('demo') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mb-4 sm:mb-6">
              <span className="font-orbitron text-xs sm:text-sm text-system-blue uppercase tracking-wider">
                ［ System Preview ］
              </span>
            </div>
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              See the System in <span className="text-system-blue">Action</span>
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-400 px-4">
              Experience the interface that will transform your life
            </p>
          </div>

          {/* Demo Video */}
          <div 
            className="relative bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 lg:p-8 overflow-hidden"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div className="aspect-video rounded-xl overflow-hidden border border-gray-700 shadow-2xl">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/YBwJ_X2G6-o?autoplay=0&mute=1&rel=0&showinfo=0&modestbranding=1"
                title="AwakenArc Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
                onLoad={() => {
                  trackEvent('demo_video_loaded', {
                    video_title: 'AwakenArc Demo Video',
                    video_source: 'youtube',
                  });
                }}
              ></iframe>
            </div>
            
            {/* Video Title */}
            <div className="mt-4 text-center">
              <h3 className="font-orbitron text-lg sm:text-xl text-white mb-2">See AwakenArc in Action</h3>
              <p className="font-rajdhani text-sm text-gray-400">
                Watch how the System transforms your daily tasks into epic quests
              </p>
            </div>
            
            {/* Glowing Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-system-blue/5 via-transparent to-purple-500/5 pointer-events-none"></div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section 
        id="pricing" 
        className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-dark-800/30"
        data-animate
        data-section-title="Pricing"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-system-blue/10 border border-system-blue/30 rounded-full mb-4 sm:mb-6">
              <span className="font-orbitron text-xs sm:text-sm text-system-blue uppercase tracking-wider">
                ［ System Access ］
              </span>
            </div>
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              Choose Your <span className="text-system-blue">Hunter Tier</span>
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-400 px-4">
              Start free and upgrade as you level up
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* Free Trial */}
            <div 
              className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 sm:p-8 hover:border-system-blue/60 transition-all duration-300"
              style={{
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="text-center mb-5 sm:mb-6">
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🎁</div>
                <h3 className="font-orbitron text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1 sm:mb-2">FREE TRIAL</h3>
                <div className="font-orbitron text-3xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">
                  FREE
                </div>
                <p className="font-rajdhani text-sm sm:text-base text-gray-400">14 days • Full features</p>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> All S-Rank Features
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> Unlimited AI Quests
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> Unlimited Programs
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> All Premium Themes
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> No Credit Card
                </li>
              </ul>

              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Start Free Trial',
                    button_location: 'pricing_section',
                    cta_type: 'trial',
                    plan_tier: 'free_trial',
                  });
                  navigate('/auth');
                }}
                className="w-full py-2.5 sm:py-3 bg-dark-800 border border-system-blue/30 text-white font-rajdhani text-sm sm:text-base font-bold rounded-lg hover:border-system-blue hover:shadow-neon-blue transition-all duration-300"
              >
                Start Free Trial
              </button>
            </div>

            {/* S-Rank Tier - Highlighted - CENTER */}
            <div 
              className="relative bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-yellow-500/60 border-r-2 border-b-2 border-r-yellow-700/80 border-b-yellow-700/80 rounded-3xl p-6 sm:p-8 transform md:scale-105"
              style={{
                boxShadow: '0 8px 24px rgba(234, 179, 8, 0.6), 0 4px 12px rgba(234, 179, 8, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              {/* Popular Badge */}
              <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 px-3 sm:px-4 py-0.5 sm:py-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-orbitron text-[10px] sm:text-xs font-bold rounded-full">
                MOST POPULAR
              </div>

              <div className="text-center mb-5 sm:mb-6">
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🥇</div>
                <h3 className="font-orbitron text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1 sm:mb-2">S-RANK HUNTER</h3>
                <div className="font-orbitron text-3xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">
                  $4.99<span className="text-base sm:text-lg text-gray-400">/mo</span>
                </div>
                <p className="font-rajdhani text-xs sm:text-sm text-gray-500 line-through mb-1">$9.99/mo</p>
                <p className="font-rajdhani text-sm sm:text-base text-green-400 font-semibold">Save 50%</p>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-yellow-500">✓</span> Unlimited AI Quests
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-yellow-500">✓</span> Unlimited AI Programs
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-yellow-500">✓</span> Unlimited Passive Skills
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-yellow-500">✓</span> All Premium Themes
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-yellow-500">✓</span> Advanced Analytics
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-yellow-500">✓</span> VIP Support
                </li>
              </ul>

              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Go Elite',
                    button_location: 'pricing_section',
                    cta_type: 'subscription',
                    plan_tier: 's_rank',
                    plan_price: 9.99,
                  });
                  navigate('/auth');
                }}
                className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-rajdhani text-sm sm:text-base font-bold rounded-lg hover:shadow-lg hover:shadow-yellow-500/50 transition-all duration-300"
              >
                Go Elite
              </button>
            </div>

            {/* Basic Tier */}
            <div 
              className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 sm:p-8 hover:border-system-blue/60 transition-all duration-300"
              style={{
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="text-center mb-5 sm:mb-6">
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">🥈</div>
                <h3 className="font-orbitron text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1 sm:mb-2">BASIC HUNTER</h3>
                <div className="font-orbitron text-3xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">
                  $2.99<span className="text-base sm:text-lg text-gray-400">/mo</span>
                </div>
                <p className="font-rajdhani text-xs sm:text-sm text-gray-500 line-through mb-1">$5.99/mo</p>
                <p className="font-rajdhani text-sm sm:text-base text-green-400 font-semibold">Save 50%</p>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> 50 AI Quests/month
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> 5 AI Programs/month
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> 10 Active Passive Skills
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> 3 Premium Themes
                </li>
                <li className="flex items-center gap-2 font-rajdhani text-sm sm:text-base text-gray-300">
                  <span className="text-green-500">✓</span> Priority Support
                </li>
              </ul>

              <button
                onClick={() => {
                  trackEvent('cta_clicked', {
                    button_text: 'Get Basic',
                    button_location: 'pricing_section',
                    cta_type: 'subscription',
                    plan_tier: 'basic',
                    plan_price: 4.99,
                  });
                  navigate('/auth');
                }}
                className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-system-blue to-blue-600 text-white font-rajdhani text-sm sm:text-base font-bold rounded-lg hover:shadow-neon-blue transition-all duration-300 transform hover:scale-105"
              >
                Get Basic
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div 
            className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            <h2 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-5 md:mb-6 px-2">
              Your <span className="text-system-blue">Awakening</span> Begins Now
            </h2>
            <p className="font-rajdhani text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-7 md:mb-8 max-w-2xl mx-auto px-2">
              The System is ready to transform your life. Your awakening begins now.
            </p>
            
            <button
              onClick={() => {
                trackEvent('cta_clicked', {
                  button_text: 'Begin Your Journey',
                  button_location: 'final_cta_section',
                  cta_type: 'primary',
                });
                navigate('/auth');
              }}
              className="w-full sm:w-auto px-8 sm:px-10 md:px-12 py-3 sm:py-3.5 md:py-4 text-white font-rajdhani text-base sm:text-lg md:text-xl font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 relative overflow-hidden"
              style={{
                background: 'linear-gradient(to bottom, rgb(59 130 246 / 0.9), rgb(37 99 235 / 0.9), rgb(30 64 175 / 0.9))',
                border: '1px solid rgba(59, 130, 246, 0.5)',
                borderTop: '2px solid rgba(100, 150, 255, 0.4)',
                borderLeft: '2px solid rgba(100, 150, 255, 0.4)',
                borderRight: '2px solid rgba(37, 99, 235, 0.6)',
                borderBottom: '2px solid rgba(37, 99, 235, 0.6)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
              <span className="relative z-10">⚡ Begin Your Journey</span>
            </button>

            <p className="font-rajdhani text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 px-2">
              No credit card required • Start free today
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-900 border-t border-gray-800 py-8 sm:py-10 md:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Brand */}
            <div className="col-span-1 sm:col-span-2">
              <div className="mb-3 sm:mb-4">
                <img 
                  src="/logo/logo.png" 
                  alt="AwakenArc Logo" 
                  className="h-12 sm:h-14 md:h-16 w-auto object-contain mb-2 sm:mb-3 opacity-80"
                />
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-orbitron text-xl sm:text-2xl font-bold text-white">
                    Awaken<span className="text-system-blue">Arc</span>
                  </span>
                </div>
              </div>
              <p className="font-rajdhani text-sm sm:text-base text-gray-400 max-w-md">
                Transform your life through the power of gamification. Complete quests, level up, and become the ultimate version of yourself.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-orbitron font-bold text-white mb-3 sm:mb-4 text-sm sm:text-base">Product</h4>
              <ul className="space-y-1.5 sm:space-y-2 font-rajdhani text-gray-400 text-sm sm:text-base">
                <li><a href="#features" className="hover:text-system-blue transition-colors inline-block py-1">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-system-blue transition-colors inline-block py-1">How It Works</a></li>
                <li><a href="#pricing" className="hover:text-system-blue transition-colors inline-block py-1">Pricing</a></li>
                <li><a href="#demo" className="hover:text-system-blue transition-colors inline-block py-1">Demo</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-orbitron font-bold text-white mb-3 sm:mb-4 text-sm sm:text-base">Support</h4>
              <ul className="space-y-1.5 sm:space-y-2 font-rajdhani text-gray-400 text-sm sm:text-base">
                <li>
                  <button 
                    onClick={() => navigate('/support')} 
                    className="hover:text-system-blue transition-colors text-left py-1"
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/support')} 
                    className="hover:text-system-blue transition-colors text-left py-1"
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 sm:pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col items-center md:items-start gap-2 sm:gap-3">
              <img 
                src="/logo/logo.png" 
                alt="AwakenArc Logo" 
                className="h-10 sm:h-12 w-auto object-contain opacity-40 hover:opacity-60 transition-opacity duration-300"
              />
              <p className="font-rajdhani text-gray-600 text-xs">
                © 2025 AwakenArc. All rights reserved.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 font-rajdhani text-gray-500 text-xs sm:text-sm">
              <button 
                onClick={() => navigate('/privacy')}
                className="hover:text-system-blue transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-system-blue transition-colors"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => navigate('/refund')}
                className="hover:text-system-blue transition-colors"
              >
                Refund Policy
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center group relative overflow-hidden"
          aria-label="Scroll to top"
          style={{
            background: 'linear-gradient(to bottom, rgb(59 130 246 / 0.9), rgb(37 99 235 / 0.9))',
            border: '1px solid rgba(59, 130, 246, 0.5)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-full"></div>
          <svg 
            className="relative z-10 w-6 h-6 transform group-hover:-translate-y-0.5 transition-transform duration-300" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}

    </div>
  );
};

export default LandingPage;


